// frontend/src/pages/CreatorsPage.tsx

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { Footer } from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';
import {
  Sparkles,
  MessageSquare,
  Globe,
  DollarSign,
  Key,
  Rocket,
  ArrowRight,
  CheckCircle2,
  LogIn,
  LogOut,
  Crown,
  User,
  Menu,
  X,
} from 'lucide-react';
import { CreditBalanceBadge } from '../components/CreditBalanceBadge';

export function CreatorsPage() {
  const { user, signOut } = useAuth();
  const { tt } = useI18n();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Scroll to top on mount and set page title
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = tt('Become a Creator') + ' - Llambada';
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const original = meta?.content || '';
    if (meta) meta.content = 'Become a Llambada creator — describe your app idea, let AI build it, then share and earn 70% revenue. No coding experience required.';
    return () => {
      document.title = 'Llambada';
      if (meta) meta.content = original;
    };
  }, [tt]);

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh">
      {/* Header */}
      <header className="border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-accent-500 to-cyan-500">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <span className="font-display text-lg font-semibold text-theme-text">
                Llambada
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-3">
              {!user && <LanguageSelector />}
              {user ? (
                <>
                  <CreditBalanceBadge />
                  <ThemeToggle />
                  <Link to="/profile" className="btn btn-ghost text-sm" title={tt('Profile & Settings')}>
                    <User className="h-4 w-4" />
                  </Link>
                  <button onClick={() => signOut()} className="btn btn-ghost text-sm" title={tt('Sign out')}>
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <ThemeToggle />
                  <Link to="/login" className="btn btn-primary text-sm">
                    <LogIn className="h-4 w-4" />
                    {tt('Sign In')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile nav */}
            <div className="flex md:hidden items-center gap-2">
              {!user && <LanguageSelector />}
              {user && <CreditBalanceBadge />}
              <ThemeToggle />
              {user ? (
                <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="btn btn-ghost p-2" aria-label={tt('Toggle menu')}>
                  {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              ) : (
                <Link to="/login" className="btn btn-primary text-sm px-3 py-2">
                  <LogIn className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {showMobileMenu && user && (
            <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
              <Link to="/profile" className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors" onClick={() => setShowMobileMenu(false)}>
                <User className="h-4 w-4" />
                {tt('Profile & Settings')}
              </Link>
              <button onClick={() => { signOut(); setShowMobileMenu(false); }} className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left">
                <LogOut className="h-4 w-4" />
                {tt('Sign out')}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/10 text-accent-500 text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            {tt('Become a Creator')}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-theme-text mb-6">
            Build Mini-Apps by Simply{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-cyan-500">
              {tt('Talking to AI')}
            </span>
          </h1>
          <p className="text-xl text-theme-text-secondary max-w-2xl mx-auto mb-8">
            {tt('No coding experience required. Just describe what you want your mini-app to do, and our AI will build it for you. Share it with the world and earn money.')}
          </p>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary btn-lg">
              <Rocket className="h-5 w-5" />
              {tt('Start Building')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          ) : (
            <Link to="/register" className="btn btn-primary btn-lg">
              <Rocket className="h-5 w-5" />
              {tt('Get Started Free')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-theme-surface/50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-theme-text text-center mb-12">
            {tt('How It Works')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card p-8 text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-6">
                <MessageSquare className="h-7 w-7 text-accent-500" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-3">
                {tt('1. Describe Your Idea')}
              </h3>
              <p className="text-theme-text-secondary">
                {tt('Simply tell the AI what you want your mini-app to do. Describe the problem it should solve or the task it should help users accomplish. No technical jargon needed.')}
              </p>
            </div>
            <div className="card p-8 text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-6">
                <Sparkles className="h-7 w-7 text-accent-500" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-3">
                {tt('2. AI Builds It')}
              </h3>
              <p className="text-theme-text-secondary">
                {tt("Our AI understands your requirements and writes all the code automatically. You can test it, refine it through conversation, and iterate until it's perfect.")}
              </p>
            </div>
            <div className="card p-8 text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-6">
                <Globe className="h-7 w-7 text-accent-500" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-3">
                {tt('3. Share & Earn')}
              </h3>
              <p className="text-theme-text-secondary">
                {tt("Publish your mini-app for the world to use. When paid subscribers use your creation, you earn a share of the revenue. It's that simple.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Sharing */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="card p-8 sm:p-12 bg-gradient-to-br from-accent-500/5 to-cyan-500/5 border-accent-500/20">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500 to-cyan-500">
                  <DollarSign className="h-10 w-10 text-white" />
                </div>
              </div>
              <div className="text-center md:text-left">
                <h2 className="font-display text-3xl font-bold text-theme-text mb-4">
                  {tt('70/30 Revenue Share')}
                </h2>
                <p className="text-lg text-theme-text-secondary mb-4">
                  {tt('When paid subscribers use your mini-apps, you earn')} <strong className="text-theme-text">70%</strong> {tt('of the attributed revenue. We keep 30% to cover infrastructure, AI costs, and platform maintenance.')}
                </p>
                <p className="text-theme-text-muted">
                  {tt('Payouts are processed monthly via Stripe Connect. Track your earnings in real-time from your dashboard.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Access */}
      <section className="py-16 bg-theme-surface/50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-theme-text text-center mb-4">
            {tt('Flexible AI Access')}
          </h2>
          <p className="text-center text-theme-text-secondary max-w-2xl mx-auto mb-12">
            {tt('Llambada provides AI capabilities to help you build, with options that fit your needs.')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-theme-surface">
                  <Sparkles className="h-6 w-6 text-accent-500" />
                </div>
                <h3 className="font-display text-xl font-semibold text-theme-text">
                  {tt('Free AI Credits')}
                </h3>
              </div>
              <p className="text-theme-text-secondary mb-4">
                {tt('Every creator gets limited free access to our code-writing AI. Perfect for getting started, experimenting with ideas, and building your first mini-apps.')}
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-theme-text-secondary">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  {tt('No credit card required to start')}
                </li>
                <li className="flex items-center gap-2 text-theme-text-secondary">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  {tt('Build and test mini-apps for free')}
                </li>
                <li className="flex items-center gap-2 text-theme-text-secondary">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  {PAYMENTS_DISABLED ? PAYMENTS_DISABLED_MESSAGE : tt('Upgrade anytime for more usage')}
                </li>
              </ul>
            </div>
            <div className="card p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-theme-surface">
                  <Key className="h-6 w-6 text-accent-500" />
                </div>
                <h3 className="font-display text-xl font-semibold text-theme-text">
                  {tt('Bring Your Own API Key')}
                </h3>
              </div>
              <p className="text-theme-text-secondary mb-4">
                {tt('Power users can connect their own Anthropic API key for unlimited AI usage. You pay Anthropic directly and get full control over your AI costs.')}
              </p>
              <div className="bg-theme-surface rounded-lg p-4 text-sm">
                <p className="font-medium text-theme-text mb-2">{tt('How to add your API key:')}</p>
                <ol className="list-decimal list-inside space-y-1 text-theme-text-secondary">
                  <li>{tt('Go to your Dashboard')}</li>
                  <li>{tt('Click on Settings (gear icon)')}</li>
                  <li>{tt('Enter your Anthropic API key')}</li>
                  <li>{tt('Save and start building unlimited')}</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="card p-8 sm:p-12 border-amber-500/20 bg-amber-500/5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Crown className="h-8 w-8 text-amber-500" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-theme-text mb-4">
                  {tt('Publishing & Earning Requirements')}
                </h2>
                <p className="text-theme-text-secondary mb-4">
                  {tt('While anyone can create and use mini-apps for personal use,')} {' '}
                  <strong className="text-theme-text">{tt('publishing more than one public mini-app and earning revenue requires a paid subscription')}</strong>.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-theme-text-secondary">
                      <strong className="text-theme-text">{tt('Free users:')}</strong> {tt('Create unlimited private mini-apps and publish one public mini-app (non-monetized)')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-theme-text-secondary">
                      <strong className="text-theme-text">{tt('Paid subscribers:')}</strong> {tt('Publish multiple mini-apps publicly and earn 70% revenue share')}
                    </span>
                  </li>
                </ul>
                {PAYMENTS_DISABLED ? (
                  <div className="p-3 rounded-lg bg-theme-surface border border-theme-border-muted text-sm text-theme-text-secondary">
                    {PAYMENTS_DISABLED_MESSAGE}
                  </div>
                ) : (
                  <Link to="/pricing" className="btn btn-primary">
                    {tt('View Pricing Plans')}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-theme-text mb-6">
            {tt('Ready to Build Your First Mini-App?')}
          </h2>
          <p className="text-xl text-theme-text-secondary max-w-2xl mx-auto mb-8">
            {tt('Join creators who are building useful tools and earning money - all without writing a single line of code.')}
          </p>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary btn-lg">
              <Sparkles className="h-5 w-5" />
              {tt('Go to Dashboard')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register" className="btn btn-primary btn-lg">
                <Sparkles className="h-5 w-5" />
                {tt('Create Free Account')}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/" className="btn btn-secondary btn-lg">
                {tt('Browse Mini-Apps')}
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer variant="compact" />
    </div>
  );
}
